/**
 * Pure utility functions for determining whether the onboarding modal
 * should auto-open on the dashboard.
 *
 * Extracted from the dashboard page component so the decision logic
 * is independently testable without rendering the full page.
 */

import type { ProductFocus } from "@/constants/onboarding";
import { PRODUCTS_REQUIRING_HOURS } from "@/constants/onboarding";
import { UserRoles } from "@/enums/user";
import type { OrgOnboardingStatus } from "@/types/org";
import type { ConfigurationStatus } from "./configDetection";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Accounts created on or after this date use stricter setup-completion checks.
 * Older accounts keep the legacy lenient behavior to avoid disrupting existing dashboards.
 */
const STRICT_ONBOARDING_CUTOFF = "2026-03-29T00:00:00.000Z";

// ---------------------------------------------------------------------------
// Individual checks
// ---------------------------------------------------------------------------

/**
 * An org is "effectively set up" if it has meaningful configuration that
 * doesn't come pre-populated (scripts ship with defaults, so we exclude them).
 *
 * Returns `false` while configStatus is still loading (`null`).
 */
export function isOrgEffectivelySetUp(configStatus: ConfigurationStatus | null): boolean {
  if (configStatus === null) return false;

  return configStatus.hasBusinessHours || configStatus.hasDirectory || configStatus.hasAssistant;
}

/**
 * For newer accounts, checks that ALL required steps for the selected focus
 * are complete — not just one or two.
 */
/** Required config keys per product focus. */
const PRODUCT_REQUIRED_KEYS: Record<ProductFocus, (keyof ConfigurationStatus)[]> = {
  inbound: ["hasDirectory", "hasInboundScript"],
  outbound: ["hasCallConcurrency", "hasOutboundScript"],
  gailgpt: ["hasAssistant"],
};

function isSetupFullyComplete(configStatus: ConfigurationStatus, gailFocus: string[]): boolean {
  const productFocus = gailFocus.filter((f): f is ProductFocus =>
    ["inbound", "outbound", "gailgpt"].includes(f)
  );

  if (productFocus.length === 0) return false;

  // Shared steps
  if (!configStatus.hasCompanyInfo) return false;
  if (!configStatus.hasNotifications) return false;

  const needsHours = productFocus.some((p) => PRODUCTS_REQUIRING_HOURS.includes(p));
  if (needsHours && !configStatus.hasBusinessHours) return false;

  // Product-specific steps
  return productFocus.every((product) =>
    PRODUCT_REQUIRED_KEYS[product].every((key) => configStatus[key])
  );
}

/**
 * Whether the empty-state dashboard should be shown.
 *
 * This drives both the UI (rendering EmptyStateDashboard) and the
 * auto-open decision, so it must remain a single source of truth.
 */
export function shouldShowEmptyState(params: {
  onboardingStatus: OrgOnboardingStatus | undefined;
  configStatus: ConfigurationStatus | null;
  viewDashboardOverride: boolean;
  orgCreatedAt?: string;
  gailFocus?: string[];
}): boolean {
  const { onboardingStatus, configStatus, viewDashboardOverride, orgCreatedAt, gailFocus } = params;

  if (onboardingStatus === "completed") return false;

  // For accounts created on or after the cutoff, require ALL focus-relevant
  // steps to be complete before hiding the empty state.
  const isNewAccount = orgCreatedAt != null && orgCreatedAt >= STRICT_ONBOARDING_CUTOFF;
  const hasCompletedRelevantSetup = isNewAccount
    ? Boolean(configStatus && gailFocus && isSetupFullyComplete(configStatus, gailFocus))
    : isOrgEffectivelySetUp(configStatus);

  if (hasCompletedRelevantSetup) return false;

  if (viewDashboardOverride) return false;

  return true;
}

// ---------------------------------------------------------------------------
// Auto-open decision
// ---------------------------------------------------------------------------

export interface AutoOpenOnboardingParams {
  /** Result of detectExistingConfiguration, or null if still loading */
  configStatus: ConfigurationStatus | null;
  /** org.details.onboarding.status */
  onboardingStatus: OrgOnboardingStatus | undefined;
  /** User clicked "View Dashboard" override on empty state */
  viewDashboardOverride: boolean;
  /** Current user role from useOrg() */
  role: string;
  /** Whether product access is still loading */
  productAccessLoading: boolean;
  /** Whether the org has GailAgent product enabled */
  gailAgentEnabled: boolean;
  /** org.createdAt — used for strict onboarding checks on newer accounts */
  orgCreatedAt?: string;
  /** org.details.onboarding.gailFocus — selected product focuses */
  gailFocus?: string[];
}

/**
 * Determines whether the onboarding modal should auto-open.
 *
 * This does NOT track "already attempted" state — the caller is responsible
 * for gating with a ref to prevent repeated opens within a session.
 */
export function shouldAutoOpenOnboarding(params: AutoOpenOnboardingParams): boolean {
  const {
    configStatus,
    onboardingStatus,
    viewDashboardOverride,
    role,
    productAccessLoading,
    gailAgentEnabled,
    orgCreatedAt,
    gailFocus,
  } = params;

  // Wait for config detection to complete before deciding.
  // Without this, configStatus=null makes isOrgEffectivelySetUp falsely
  // return false, causing the modal to flash open for configured orgs.
  if (configStatus === null) return false;

  // Must be in the empty-state dashboard
  const showEmptyState = shouldShowEmptyState({
    onboardingStatus,
    configStatus,
    viewDashboardOverride,
    orgCreatedAt,
    gailFocus,
  });
  if (!showEmptyState) return false;

  // User explicitly skipped — don't re-open the modal they dismissed
  if (onboardingStatus === "skipped") return false;

  // Role and product access guards
  if (role !== UserRoles.Admin) return false;
  if (productAccessLoading) return false;
  if (!gailAgentEnabled) return false;

  return true;
}

/**
 * Whether the dashboard should keep its loading skeleton.
 *
 * Config detection only runs for agent users. Without that gate, new trial
 * accounts that have no product roles yet wait forever on a null configStatus.
 */
export function shouldWaitForDashboardConfig(params: {
  hasCompletedOnboarding: boolean;
  configStatus: ConfigurationStatus | null;
  hasAgentAccess: boolean;
  /** `useOrg().productAccess.loading`: false only once product roles and org data are both in. */
  productAccessLoading: boolean;
}): boolean {
  if (params.hasCompletedOnboarding) return false;
  if (params.configStatus !== null) return false;
  if (params.productAccessLoading) return true;
  return params.hasAgentAccess;
}

/**
 * Whether a dashboard visitor should be sent to /get-gail to start or resume
 * trial provisioning. Self-serve org create writes no products at all; Stripe
 * assigns them after /get-gail consumes the pending checkout payload.
 *
 * Deliberately narrow: an org whose subscription lapsed still has product
 * records (canceled, past_due, ...) and is handled by the billing banner, not
 * by bouncing every dashboard visit. Leftover `pendingPaymentData` is unscoped
 * and is not used here; it can survive an aborted handoff and is not proof
 * the current org should check out. Users who cannot start checkout stay on
 * the dashboard rather than landing on the /get-gail access-denied view.
 */
export function shouldRedirectToTrialCheckout(params: {
  productAccessLoading: boolean;
  orgHasActiveProducts: boolean;
  /** True when the org has any product record, active or not. */
  orgHasAnyProducts: boolean;
  /** Includes GailGPT V1 and reporting-only access, which carry no product record. */
  userHasAnyProductAccess: boolean;
  /** Same gate /get-gail uses to allow checkout: Settings update permission. */
  canStartCheckout: boolean;
  onboardingStatus: OrgOnboardingStatus | undefined;
}): boolean {
  if (params.productAccessLoading) return false;
  if (params.orgHasActiveProducts) return false;
  if (params.userHasAnyProductAccess) return false;
  if (!params.canStartCheckout) return false;
  if (params.orgHasAnyProducts) return false;
  if (params.onboardingStatus === "completed" || params.onboardingStatus === "skipped") {
    return false;
  }
  return true;
}
